/**
 * components/wishlist/wishlist-item-row.tsx
 *
 * Canonical item row per Section 12.3 of design-language.md.
 * Used in wishlist-vibe.tsx list.
 * Supports long-press → context menu and tap → detail sheet.
 */
import { useRef } from 'react'
import type { WishlistItem, WishlistVibe } from '@just-us/shared'
import LinkPreview from './link-preview'
import { VIBE_META } from './vibe-chip'

interface WishlistItemRowProps {
  item: WishlistItem
  onToggleDone: (item: WishlistItem) => void
  onOpenDetail: (item: WishlistItem) => void
  onOpenContextMenu: (item: WishlistItem) => void
}

const isWithin24h = (ts: number) => Date.now() - ts * 1000 < 86_400_000

export default function WishlistItemRow({
  item,
  onToggleDone,
  onOpenDetail,
  onOpenContextMenu,
}: WishlistItemRowProps) {
  const meta = VIBE_META[item.vibe as WishlistVibe]
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePointerDown = () => {
    longPressTimer.current = setTimeout(() => {
      onOpenContextMenu(item)
    }, 500)
  }

  const handlePointerUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  return (
    <div
      className="
        flex items-center gap-3 py-3
        border-b border-[#F9E0E8] last:border-b-0
        active:bg-[#FBEAF0]
        transition-colors duration-100
        touch-manipulation
        cursor-pointer
      "
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onClick={() => onOpenDetail(item)}
    >
      {/* Left: vibe-colored checkbox */}
      <button
        type="button"
        className="
          w-[26px] h-[26px] rounded-full flex-shrink-0
          border-[1.5px] flex items-center justify-center
          transition-all duration-200
          active:scale-[0.90]
          touch-manipulation
        "
        style={{
          borderColor: meta.accent,
          background: item.doneAt ? meta.accent : 'transparent',
        }}
        onClick={(e) => {
          e.stopPropagation()
          onToggleDone(item)
        }}
      >
        {item.doneAt && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 6l3 3 5-5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {/* Center: title + attribution + optional link preview */}
      <div className="flex-1 min-w-0">
        <p
          className={`font-['DM_Sans'] text-[15px] text-[#2C1810] truncate ${
            item.doneAt ? 'line-through text-[#B89AA2]' : ''
          }`}
        >
          {item.title}
        </p>

        {/* Attribution — always shown */}
        <div className="flex items-center gap-[5px] mt-[2px]">
          {!item.isAddedByMe && (
            <div className="w-[14px] h-[14px] rounded-full bg-[#FBEAF0] border border-[#F4C0D1] flex items-center justify-center text-[8px] flex-shrink-0">
              {item.addedByAvatar}
            </div>
          )}
          <span className="font-['DM_Sans'] text-[11px] italic text-[#B89AA2]">
            {item.isAddedByMe ? 'Added by you' : `Added by ${item.addedByNickname}`}
          </span>
          {item.nudgedAt && isWithin24h(item.nudgedAt) && (
            <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2] ml-1">
              · 👀 nudged
            </span>
          )}
        </div>

        {/* Optional note */}
        {item.note && (
          <p className="font-['DM_Sans'] text-[12px] text-[#7A5A62] mt-[2px] truncate">
            {item.note}
          </p>
        )}

        {/* Optional link preview chip */}
        {item.linkUrl && (
          <LinkPreview
            linkUrl={item.linkUrl}
            linkDomain={item.linkDomain}
            linkFavicon={item.linkFavicon}
            linkTitle={item.linkTitle}
          />
        )}
      </div>

      {/* Right: chevron */}
      <span className="text-[#F4C0D1] text-[14px] flex-shrink-0">›</span>
    </div>
  )
}
